import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Hero from './Hero';
import About from './About';
import Services from './Services';
import Process from './Process';
import Pricing from './Pricing';
import Reviews from './Reviews';
import Contact from './Contact';
import Faq from './Faq';
import NotFound from './NotFound';
import Privacy from './Privacy';
import Terms from './Terms';
import Cookies from './Cookies';
import PageTransition from './components/PageTransition';
import './App.css';

// AnimatePresence requires a non-static wrapper, so we need a separate component
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/contact" element={
          <PageTransition>
            <Contact />
          </PageTransition>
        } />
        <Route path="/faq" element={
          <PageTransition>
            <div className="pt-20"><Faq /></div>
          </PageTransition>
        } />
        <Route path="/privacy" element={
          <PageTransition>
            <Privacy />
          </PageTransition>
        } />
        <Route path="/terms" element={
          <PageTransition>
            <Terms />
          </PageTransition>
        } />
        <Route path="/cookies" element={
          <PageTransition>
            <Cookies />
          </PageTransition>
        } />
        <Route path="/" element={
          <PageTransition>
            <main className="pt-20">
              <section id="hero">
                <Hero />
              </section>
              
              <section id="about">
                <About />
              </section>
              
              <section id="services">
                <Services />
              </section>
              
              <section id="process">
                <Process />
              </section>
              
              <section id="pricing">
                <Pricing />
              </section>
              
              <section id="testimonials">
                <Reviews />
              </section>
              
              <section id="faq" className="-mt-20">
                <Faq />
              </section>
            </main>
          </PageTransition>
        } />
        {/* Add 404 not found route - important for SEO */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 overflow-x-hidden relative w-full">
        {/* Navigation placeholder - a simple fixed header with transparent blur effect */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/40 backdrop-blur-lg py-4 border-b border-slate-800/30">
          <div className="container mx-auto px-4 flex justify-between items-center">
            <Link to="/" className="text-2xl font-bold" aria-label="forca.dev Home">
              <span 
                className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent shine-effect text-3xl" 
                data-text="Wesley">Wesley</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8" aria-label="Main Navigation">
              <a href="/#about" className="text-slate-300 hover:text-cyan-500 transition-colors" rel="nofollow">About</a>
              <a href="/#services" className="text-slate-300 hover:text-cyan-500 transition-colors" rel="nofollow">Services</a>
              <a href="/#process" className="text-slate-300 hover:text-cyan-500 transition-colors" rel="nofollow">Process</a>
              <a href="/#pricing" className="text-slate-300 hover:text-cyan-500 transition-colors" rel="nofollow">Pricing</a>
              <a href="/#testimonials" className="text-slate-300 hover:text-cyan-500 transition-colors" rel="nofollow">Testimonials</a>
              <a href="/#faq" className="text-slate-300 hover:text-cyan-500 transition-colors" rel="nofollow">FAQ</a>
              <Link to="/contact" className="text-slate-300 hover:text-cyan-500 transition-colors">Contact</Link>
            </nav>
            <Link to="/contact" className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 p-px font-semibold text-white shadow-[0_1000px_0_0_hsl(0_0%_100%_/_0%)_inset] transition-colors hover:shadow-[0_1000px_0_0_hsl(0_0%_100%_/_2%)_inset]" aria-label="Contact Me">
              <div className="relative rounded-xl bg-slate-950/50 px-4 py-2 transition-colors group-hover:bg-transparent">
                <span className="relative flex items-center justify-center gap-2">
                  Contact Me
                  <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">
                    <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                  </svg>
                </span>
              </div>
            </Link>
          </div>
        </header>

        <AnimatedRoutes />
        
        {/* Simple compact footer */}
        <footer className="relative border-t border-slate-800/50 py-6 overflow-hidden">
          {/* Background gradient elements */}
          <div className="absolute -top-40 -left-40 h-60 w-60 rounded-full bg-gradient-to-br from-cyan-500/10 to-blue-500/5 blur-3xl"></div>
          <div className="absolute -bottom-20 -right-20 h-60 w-60 rounded-full bg-gradient-to-br from-blue-500/10 to-cyan-500/5 blur-3xl"></div>
          
          <div className="container mx-auto px-4">
            {/* Simple two row footer */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-4">
              {/* Brand section */}
              <div className="mb-4 md:mb-0">
                <div className="text-xl font-bold text-white">
                  <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">Wesley</span>
                </div>
                <p className="text-slate-400 text-xs mt-1">Web Development Solutions</p>
              </div>
              
              {/* Essential links */}
              <div className="flex flex-wrap justify-center gap-6">
                <a href="/#about" className="text-slate-400 hover:text-cyan-400 transition-colors text-xs">About</a>
                <a href="/#services" className="text-slate-400 hover:text-cyan-400 transition-colors text-xs">Services</a>
                <a href="/#process" className="text-slate-400 hover:text-cyan-400 transition-colors text-xs">Process</a>
                <a href="/#pricing" className="text-slate-400 hover:text-cyan-400 transition-colors text-xs">Pricing</a>
                <a href="/contact" className="text-slate-400 hover:text-cyan-400 transition-colors text-xs">Contact</a>
              </div>
            </div>
            
            {/* Bottom section with copyright and legal links */}
            <div className="pt-4 border-t border-slate-800/30 flex flex-col md:flex-row justify-between items-center gap-3">
              <div className="text-xs text-slate-500">
                © {new Date().getFullYear()} Wesley. All rights reserved.
              </div>
              
              <div className="flex gap-4">
                <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-400 transition-colors">Privacy</a>
                <a href="/terms" className="text-xs text-slate-500 hover:text-cyan-400 transition-colors">Terms</a>
                <a href="/cookies" className="text-xs text-slate-500 hover:text-cyan-400 transition-colors">Cookies</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
